import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search, Building2, Mail, Phone, Pencil, User, Globe, Briefcase, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import { useConfirm } from '@/components/ConfirmDialog';
import { DocumentsSection } from '@/components/documents/DocumentsSection';
import { AddableCombobox } from '@/components/AddableCombobox';
import { useOptionSet } from '@/lib/useOptionSet';
import api from '@/lib/api';

const JOB_TITLES = [
  'Building Inspector', 'Building Permits Examiner', 'Clerk', 'Design Engineer',
  'Environmental Analyst', 'Fire Plan Inspector', 'Gas Inspector', 'Inspector',
  'Plan Examiner', 'Plumbing Inspector', 'ZBA',
];

const JURISDICTIONS = [
  'Babylon', 'Brookhaven', 'Brooklyn', 'City of Yonkers', 'FDNY', 'Hampstead',
  'Manhattan TOPO', 'North Hampstead', 'Oyster Bay', 'PSEG', 'Queens',
  'Riverhead', 'Town of Huntington', 'Town of Islip', 'Village of East Hill',
  'Village of Flower Hill', 'Village of Freeport',
];

const CLIENT_TYPES = ['Residential', 'Commercial'];

// ─── Clients Tab ───
function ClientsTab({ clients, contacts, onRefresh }) {
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [form, setForm] = useState({});

  const openCreate = () => {
    setEditClient(null);
    setClientDetail(null);
    setForm({ name: '', email: '', phone: '', address: '', client_type: '', primary_contact_id: '', billable_contact_id: '' });
    setSheetOpen(true);
  };

  const openEdit = async (c) => {
    setEditClient(c);
    setForm({
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      address: c.address || '', client_type: c.client_type || '',
      primary_contact_id: c.primary_contact_id || '',
      billable_contact_id: c.billable_contact_id || '',
    });
    // Fetch full detail with enriched contact info
    try {
      const r = await api.get(`/clients/${c.id}`);
      setClientDetail(r.data);
    } catch { setClientDetail(null); }
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Client name required');
    const payload = {
      ...form,
      primary_contact_id: form.primary_contact_id || null,
      billable_contact_id: form.billable_contact_id || null,
    };
    try {
      if (editClient) { await api.put(`/clients/${editClient.id}`, payload); toast.success('Client updated'); }
      else { await api.post('/clients', payload); toast.success('Client created'); }
      setSheetOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const filtered = clients.filter(c => (c.name || '').toLowerCase().includes(search.toLowerCase()));

  const ContactInfoBlock = ({ label, detail }) => {
    if (!detail || !detail.name) return null;
    return (
      <div className="p-3 rounded-md border border-border bg-background space-y-0.5">
        <p className="text-sm"><span style={{ color: '#031333' }}>{label}: </span><span style={{ color: '#010A21' }}>{detail.name}</span></p>
        {detail.job_title && <p className="text-sm"><span style={{ color: '#031333' }}>Role: </span><span style={{ color: '#010A21' }}>{detail.job_title}</span></p>}
        {detail.email && <p className="text-sm"><span style={{ color: '#031333' }}>Email: </span><span style={{ color: '#010A21' }}>{detail.email}</span></p>}
        {detail.phone && <p className="text-sm"><span style={{ color: '#031333' }}>Phone: </span><span style={{ color: '#010A21' }}>{detail.phone}</span></p>}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search clients..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="clients-search" />
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground ml-3" data-testid="create-client-button">
          <Plus className="h-4 w-4 mr-1" /> New Client
        </Button>
      </div>

      <div data-testid="clients-grid">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Name</TableHead>
              <TableHead className="text-xs text-muted-foreground">Type</TableHead>
              <TableHead className="text-xs text-muted-foreground">Primary Contact</TableHead>
              <TableHead className="text-xs text-muted-foreground">Phone</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.id} className="group border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openEdit(c)} data-testid={`client-card-${c.id}`}>
                <TableCell className="text-sm font-medium" style={{ color: '#010A21' }}>{c.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.client_type || '\u00A0'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.primary_contact_name || '\u00A0'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.phone || '\u00A0'}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-client-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No clients found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">{editClient ? 'Edit Client' : 'New Client'}</SheetTitle><SheetDescription>Client information</SheetDescription></SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Company Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="client-form-name" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Client Type</Label>
              <Select value={form.client_type || '__none'} onValueChange={v => setForm({ ...form, client_type: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="client-form-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="__none">None</SelectItem>
                  {CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" /></div>
            <div><Label className="text-xs text-muted-foreground">Address</Label><ExpandableTextarea value={form.address || ''} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-background border-border" rows={2} expandedRows={5} /></div>

            <div>
              <Label className="text-xs text-muted-foreground">Primary Contact</Label>
              <Select value={form.primary_contact_id || '__none'} onValueChange={v => setForm({ ...form, primary_contact_id: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="client-form-primary-contact"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-60">
                  <SelectItem value="__none">None</SelectItem>
                  {contacts.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}{ct.job_title ? ` (${ct.job_title})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Billable Contact</Label>
              <Select value={form.billable_contact_id || '__none'} onValueChange={v => setForm({ ...form, billable_contact_id: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="client-form-billable-contact"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-60">
                  <SelectItem value="__none">None</SelectItem>
                  {contacts.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}{ct.job_title ? ` (${ct.job_title})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Inline Contact Detail Display */}
            {editClient && clientDetail && (
              <div className="space-y-2" data-testid="client-contact-details">
                <ContactInfoBlock label="Primary Contact" detail={clientDetail.primary_contact_detail} />
                <ContactInfoBlock label="Billable Contact" detail={clientDetail.billable_contact_detail} />
              </div>
            )}

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="client-form-submit">{editClient ? 'Update Client' : 'Create Client'}</Button>

            {editClient && (
              <div className="pt-4 mt-2 border-t border-border">
                <p className="text-sm font-semibold mb-2" style={{ color: '#010A21' }}>Documents</p>
                <DocumentsSection recordType="Client" recordId={editClient.id} />
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Contacts Tab ───
function ContactsTab({ contacts, onRefresh, townOnly }) {
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState({});
  const { options: jobTitleOptions, addOption: addJobTitle } = useOptionSet(
    'job_title', JOB_TITLES, contacts.map(c => c.job_title).filter(Boolean)
  );

  const displayed = townOnly
    ? contacts.filter(c => c.jurisdiction && c.jurisdiction.trim() !== '')
    : contacts;

  const filtered = displayed.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.job_title || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.jurisdiction || '').toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditContact(null);
    setForm({ name: '', email: '', phone: '', job_title: '', website: '', jurisdiction: '', comments: '' });
    setSheetOpen(true);
  };
  const openEdit = (c) => { setEditContact(c); setForm({ ...c }); setSheetOpen(true); };

  const handleSave = async () => {
    if (!form.name?.trim()) return toast.error('Contact name is required');
    try {
      if (editContact) { await api.put(`/contacts/${editContact.id}`, form); toast.success('Contact updated'); }
      else { await api.post('/contacts', form); toast.success('Contact created'); }
      setSheetOpen(false); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async () => {
    if (!editContact) return;
    const ok = await confirm({ title: 'Delete contact', description: `Delete contact "${editContact.name}"?`, confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    await api.delete(`/contacts/${editContact.id}`);
    toast.success('Contact deleted');
    setSheetOpen(false);
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="contacts-search" />
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground ml-3" data-testid="create-contact-button">
          <Plus className="h-4 w-4 mr-1" /> New Contact
        </Button>
      </div>

      <div data-testid="contacts-grid">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Contact Name</TableHead>
              <TableHead className="text-xs text-muted-foreground">Role / Job</TableHead>
              <TableHead className="text-xs text-muted-foreground">Email</TableHead>
              <TableHead className="text-xs text-muted-foreground">Phone</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.id} className="group border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openEdit(c)} data-testid={`contact-card-${c.id}`}>
                <TableCell className="text-sm font-medium" style={{ color: '#010A21' }}>
                  {c.name}
                  {c.jurisdiction && <span className="text-xs text-muted-foreground ml-2">{c.jurisdiction}</span>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.job_title || '\u00A0'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.email || '\u00A0'}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.phone || '\u00A0'}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-contact-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">{townOnly ? 'No town contacts found' : 'No contacts found'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg bg-card border-border overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-heading">{editContact ? 'Edit Contact' : 'New Contact'}</SheetTitle>
            <SheetDescription>Contact information</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 mt-4">
            <div><Label className="text-xs text-muted-foreground">Contact Name *</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-background border-border" data-testid="contact-form-name" /></div>
            <div><Label className="text-xs text-muted-foreground">Email</Label><Input value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-background border-border" data-testid="contact-form-email" /></div>
            <div><Label className="text-xs text-muted-foreground">Phone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-background border-border" data-testid="contact-form-phone" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Job Title</Label>
              <AddableCombobox
                value={form.job_title || ''}
                onChange={(v) => setForm({ ...form, job_title: v })}
                options={jobTitleOptions}
                onCreate={addJobTitle}
                placeholder="Select or add a job title"
                testid="contact-form-job-title"
              />
            </div>
            <div><Label className="text-xs text-muted-foreground">Website</Label><Input value={form.website || ''} onChange={e => setForm({ ...form, website: e.target.value })} className="bg-background border-border" data-testid="contact-form-website" placeholder="https://" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Jurisdiction</Label>
              <Select value={form.jurisdiction || '__none'} onValueChange={v => setForm({ ...form, jurisdiction: v === '__none' ? '' : v })}>
                <SelectTrigger className="bg-background border-border" data-testid="contact-form-jurisdiction"><SelectValue placeholder="Select jurisdiction" /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-60">
                  <SelectItem value="__none">None</SelectItem>
                  {JURISDICTIONS.map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-muted-foreground">Comments</Label><ExpandableTextarea value={form.comments || ''} onChange={e => setForm({ ...form, comments: e.target.value })} className="bg-background border-border" rows={3} expandedRows={8} data-testid="contact-form-comments" /></div>

            <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground mt-2" data-testid="contact-form-submit">{editContact ? 'Update Contact' : 'Create Contact'}</Button>
            {editContact && (
              <Button variant="outline" onClick={handleDelete} className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 gap-1.5" data-testid="contact-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete Contact
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Main Page ───
export default function ClientsPage() {
  const [pageTab, setPageTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);

  const fetchClients = useCallback(() => api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])), []);
  const fetchContacts = useCallback(() => api.get('/contacts').then(r => setContacts(Array.isArray(r.data) ? r.data : [])), []);

  useEffect(() => { fetchClients(); fetchContacts(); }, [fetchClients, fetchContacts]);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="clients-page">
      <div>
        <h1 className="font-heading font-semibold text-lg tracking-tight">Clients & Contacts</h1>
        <p className="text-sm text-muted-foreground">{clients.length} clients, {contacts.length} contacts</p>
      </div>

      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList className="bg-secondary border border-border" data-testid="clients-page-tabs">
          <TabsTrigger value="clients" className="text-xs data-[state=active]:bg-background gap-1" data-testid="tab-clients">
            <Building2 className="h-3 w-3" /> Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="all-contacts" className="text-xs data-[state=active]:bg-background gap-1" data-testid="tab-all-contacts">
            <User className="h-3 w-3" /> All Contacts ({contacts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="clients" className="mt-3">
          <ClientsTab clients={clients} contacts={contacts} onRefresh={fetchClients} />
        </TabsContent>

        <TabsContent value="all-contacts" className="mt-3">
          <ContactsTab contacts={contacts} onRefresh={fetchContacts} townOnly={false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
